import React, { useState, useMemo, useRef, useEffect } from 'react';
import type { ContactInformationData } from '../../../types/customer-portal';
import {
  COUNTRY_CODES,
  getDefaultCountryCode,
  autoFormatPhoneNumber,
  validatePhoneNumber,
  getCleanPhoneNumber,
  validateLandlineNumberWithLimit,
  LANDLINE_NUMBER_MAX_LENGTH
} from '../../../../../shared/utils/phoneUtils';
import './ContactInformationStep.css';

// Field validation highlighting interface
interface FieldValidationErrors {
  [key: string]: boolean;
}

interface ContactInformationStepProps {
  data?: ContactInformationData & {
    landlineCountryCode?: string;
    landlinePhoneNumber?: string;
    formattedLandlinePhoneNumber?: string;
  };
  onNext: (data: ContactInformationData & {
    landlineCountryCode?: string;
    landlinePhoneNumber?: string;
    formattedLandlinePhoneNumber?: string;
  }) => void;
  onBack: () => void;
}

const ContactInformationStep: React.FC<ContactInformationStepProps> = ({
  data,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState({
    // Mobile number (required)
    contactType: 'mobile' as const,
    countryCode: data?.countryCode || getDefaultCountryCode().value,
    phoneNumber: data?.phoneNumber || '',
    formattedPhoneNumber: data?.formattedPhoneNumber || '',
    // Landline number (optional)
    landlineCountryCode: data?.landlineCountryCode || getDefaultCountryCode().value,
    landlinePhoneNumber: data?.landlinePhoneNumber || '',
    formattedLandlinePhoneNumber: data?.formattedLandlinePhoneNumber || ''
  });

  const [isMobileCountryDropdownOpen, setIsMobileCountryDropdownOpen] = useState(false);
  const [isLandlineCountryDropdownOpen, setIsLandlineCountryDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Field validation highlighting state
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  const mobileCountryDropdownRef = useRef<HTMLDivElement>(null);
  const landlineCountryDropdownRef = useRef<HTMLDivElement>(null);
  const mobilePhoneInputRef = useRef<HTMLInputElement>(null);
  const landlinePhoneInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileCountryDropdownRef.current && !mobileCountryDropdownRef.current.contains(event.target as Node)) {
        setIsMobileCountryDropdownOpen(false);
      }
      if (landlineCountryDropdownRef.current && !landlineCountryDropdownRef.current.contains(event.target as Node)) {
        setIsLandlineCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get current country code info
  const currentMobileCountryCode = useMemo(() => {
    return COUNTRY_CODES.find(cc => cc.value === formData.countryCode) || getDefaultCountryCode();
  }, [formData.countryCode]);

  const currentLandlineCountryCode = useMemo(() => {
    return COUNTRY_CODES.find(cc => cc.value === formData.landlineCountryCode) || getDefaultCountryCode();
  }, [formData.landlineCountryCode]);

  // Get validation errors for required fields (for highlighting)
  const getValidationErrors = (): FieldValidationErrors => {
    const errors: FieldValidationErrors = {};
    
    // Mobile number is required
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = true;
    }
    
    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? 'field-error' : '';
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleMobileCountryCodeSelect = (countryCode: string) => {
    setFormData(prev => {
      const newData = { ...prev, countryCode };
      
      // Reformat phone number with new country code
      if (prev.phoneNumber) {
        const includeCountryCode = countryCode !== '+63';
        const formatResult = autoFormatPhoneNumber(prev.phoneNumber, 'mobile', includeCountryCode);
        newData.formattedPhoneNumber = formatResult.formatted;
      }
      
      return newData;
    });
    setIsMobileCountryDropdownOpen(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.phoneNumber;
      return newErrors;
    });
  };

  const handleLandlineCountryCodeSelect = (countryCode: string) => {
    setFormData(prev => {
      const newData = { ...prev, landlineCountryCode: countryCode };
      
      // Reformat phone number with new country code
      if (prev.landlinePhoneNumber) {
        const includeCountryCode = countryCode !== '+63';
        const formatResult = autoFormatPhoneNumber(prev.landlinePhoneNumber, 'landline', includeCountryCode);
        newData.formattedLandlinePhoneNumber = formatResult.formatted;
      }
      
      return newData;
    });
    setIsLandlineCountryDropdownOpen(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.landlinePhoneNumber;
      return newErrors;
    });
  };

  // Input sanitization function to prevent non-phone characters
  const sanitizePhoneInput = (input: string): string => {
    return input.replace(/[^0-9\s\-()+ ]/g, '');
  };

  const handleMobilePhoneNumberChange = (value: string) => {
    // Allow only digits and + symbol for international format
    const sanitizedValue = value.replace(/[^0-9+]/g, '');
    
    // Clear field error when user starts typing
    clearFieldError('phoneNumber');
    
    // ✅ Dynamic character limit based on format
    let maxLength = 13; // Default to international format
    
    if (sanitizedValue.startsWith('09')) {
      maxLength = 11; // Local format: 09XXXXXXXXX
    } else if (sanitizedValue.startsWith('+63')) {
      maxLength = 13; // International format: +639XXXXXXXXX
    }
    
    // Check if input exceeds the appropriate limit
    if (sanitizedValue.length > maxLength) {
      return; // Stop updating if exceeded
    }

    // ✅ Update form state with raw value (no formatting)
    setFormData(prev => ({
      ...prev,
      phoneNumber: sanitizedValue,
      formattedPhoneNumber: sanitizedValue, // Store same value without formatting
      contactNumber: sanitizedValue // Legacy field
    }));

    // ✅ Clear validation errors if user is retyping
    if (errors.phoneNumber || errors.contactNumber) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        delete newErrors.contactNumber;
        return newErrors;
      });
    }
  };

  const handleLandlinePhoneNumberChange = (value: string) => {
    // Sanitize input first - remove any non-phone characters
    const sanitizedValue = sanitizePhoneInput(value);
    
    // Remove any existing formatting to get clean input
    const cleanValue = sanitizedValue.replace(/[\s\-()+]/g, '');
    
    const includeCountryCode = formData.landlineCountryCode !== '+63';
    const formatResult = autoFormatPhoneNumber(cleanValue, 'landline', includeCountryCode);
    
    // ✅ Enforce character limit using new validation
    if (formatResult.formatted.length > LANDLINE_NUMBER_MAX_LENGTH) {
      return; // Stop updating if exceeded
    }
    
    setFormData(prev => ({
      ...prev,
      landlinePhoneNumber: cleanValue,
      formattedLandlinePhoneNumber: formatResult.formatted
    }));

    // Clear errors when user starts typing
    if (errors.landlinePhoneNumber) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.landlinePhoneNumber;
        return newErrors;
      });
    }
  };

  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Mobile number is required
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Mobile number is required';
    } else {
      // Validate format: 09XXXXXXXXX (11 chars) or +639XXXXXXXXX (13 chars)
      const phoneNumber = formData.phoneNumber.trim();
      
      if (phoneNumber.startsWith('+639') && phoneNumber.length === 13) {
        // International format: +639XXXXXXXXX
        const digits = phoneNumber.substring(3); // Remove +63
        if (!/^9\d{9}$/.test(digits)) {
          newErrors.phoneNumber = 'Please enter a valid Philippine mobile number (+639XXXXXXXXX)';
        }
      } else if (phoneNumber.startsWith('09') && phoneNumber.length === 11) {
        // Local format: 09XXXXXXXXX
        if (!/^09\d{9}$/.test(phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid Philippine mobile number (09XXXXXXXXX)';
        }
      } else {
        newErrors.phoneNumber = 'Mobile number must be 11 digits (09XXXXXXXXX) or 13 characters (+639XXXXXXXXX)';
      }
    }

    // Landline number is optional, but if provided, must be valid
    if (formData.landlinePhoneNumber.trim()) {
      // Check character limit first
      if (!validateLandlineNumberWithLimit(formData.formattedLandlinePhoneNumber)) {
        newErrors.landlinePhoneNumber = 'Landline number exceeds character limit';
      } else {
        const cleanLandlineNumber = getCleanPhoneNumber(formData.landlinePhoneNumber);
        const fullLandlineNumber = formData.landlineCountryCode === '+63' ? formData.landlinePhoneNumber : `${formData.landlineCountryCode}${cleanLandlineNumber}`;
        
        if (!validatePhoneNumber(fullLandlineNumber, 'landline')) {
          newErrors.landlinePhoneNumber = 'Please enter a valid Philippine landline number';
        }
      }
    }

    return newErrors;
  };

  const handleNext = () => {
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      
      // Also set error messages for display
      const errorMessages = validateForm();
      setErrors(errorMessages);
      
      return; // Don't proceed if there are validation errors
    }
    
    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);
    setErrors({});

    const finalData = {
      contactType: formData.contactType,
      countryCode: formData.countryCode,
      phoneNumber: formData.phoneNumber,
      formattedPhoneNumber: formData.formattedPhoneNumber,
      landlineCountryCode: formData.landlineCountryCode,
      landlinePhoneNumber: formData.landlinePhoneNumber,
      formattedLandlinePhoneNumber: formData.formattedLandlinePhoneNumber
    };
    
    onNext(finalData);
  };

  const getMobilePhoneNumberPlaceholder = () => {
    return '+639';
  };

  const getLandlinePhoneNumberPlaceholder = () => {
    return formData.landlineCountryCode === '+63' ? '(02) 8230-8511' : '+63 2 8230-8511';
  };

  return (
    <>
      <div className="contact-information-step">
        <div className="step-header">
          <h3 className="step-title">Contact Information</h3>
          <p className="step-description">
            Please provide the contact details of the Principal Insured.
          </p>
        </div>

        <div className=" step-form contact-information-step-form">
          {/* Mobile Number Input Section */}
          <div className="phone-input-section visible">
            <div className={`form-group ${getFieldErrorClass('phoneNumber')}`}>
              <label className="form-label">
                Mobile Number *
              </label>
              
              <div className={`phone-input-container ${errors.phoneNumber ? 'error' : ''}`}>
                {/* Country Code Dropdown */}
                <div 
                  className={`country-code-dropdown ${isMobileCountryDropdownOpen ? 'open' : ''}`}
                  ref={mobileCountryDropdownRef}
                >
                  <button
                    type="button"
                    className="country-code-trigger"
                    onClick={() => setIsMobileCountryDropdownOpen(!isMobileCountryDropdownOpen)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsMobileCountryDropdownOpen(!isMobileCountryDropdownOpen);
                      }
                    }}
                  >
                    <span>{currentMobileCountryCode.code}</span>
                    <svg 
                      className="dropdown-arrow" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isMobileCountryDropdownOpen && (
                    <div className="country-code-options">
                      {COUNTRY_CODES.map((country) => (
                        <div
                          key={country.code}
                          className={`country-code-option ${country.value === formData.countryCode ? 'selected' : ''}`}
                          onClick={() => handleMobileCountryCodeSelect(country.value)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleMobileCountryCodeSelect(country.value);
                            }
                          }}
                        >
                          {country.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Phone Number Input */}
                <input
                  ref={mobilePhoneInputRef}
                  type="tel"
                  pattern="[0-9\s\-\(\)\+]*"
                  className="phone-number-input"
                  value={formData.formattedPhoneNumber}
                  onChange={(e) => handleMobilePhoneNumberChange(e.target.value)}
                  placeholder={getMobilePhoneNumberPlaceholder()}
                />
              </div>
            </div>
          </div>

          {/* Landline Number Input Section */}
          <div className="phone-input-section visible">
            <div className="form-group">
              <label className="form-label">
                Landline Number
              </label>
              
              <div className={`phone-input-container ${errors.landlinePhoneNumber ? 'error' : ''}`}>
                {/* Country Code Dropdown */}
                <div 
                  className={`country-code-dropdown ${isLandlineCountryDropdownOpen ? 'open' : ''}`}
                  ref={landlineCountryDropdownRef}
                >
                  <button
                    type="button"
                    className="country-code-trigger"
                    onClick={() => setIsLandlineCountryDropdownOpen(!isLandlineCountryDropdownOpen)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsLandlineCountryDropdownOpen(!isLandlineCountryDropdownOpen);
                      }
                    }}
                  >
                    <span>{currentLandlineCountryCode.code}</span>
                    <svg 
                      className="dropdown-arrow" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isLandlineCountryDropdownOpen && (
                    <div className="country-code-options">
                      {COUNTRY_CODES.map((country) => (
                        <div
                          key={country.code}
                          className={`country-code-option ${country.value === formData.landlineCountryCode ? 'selected' : ''}`}
                          onClick={() => handleLandlineCountryCodeSelect(country.value)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleLandlineCountryCodeSelect(country.value);
                            }
                          }}
                        >
                          {country.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Landline Phone Number Input */}
                <input
                  ref={landlinePhoneInputRef}
                  type="tel"
                  pattern="[0-9\s\-\(\)\+]*"
                  className="phone-number-input"
                  value={formData.formattedLandlinePhoneNumber}
                  onChange={(e) => handleLandlinePhoneNumberChange(e.target.value)}
                  placeholder={getLandlinePhoneNumberPlaceholder()}
                />
              </div>
              
              {errors.landlinePhoneNumber && <span className="error-message">{errors.landlinePhoneNumber}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="btn-next"
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </>
  );
};

export default ContactInformationStep;
